import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/api';
import logoIglesia from '../../assets/images/logo-oficial.png';

interface LoginFormProps {
  onSubmit?: (data: { email: string; password: string }) => void;
}

// Determina la ruta inicial del usuario según sus permisos
function getInitialRouteForUser(user: any): string {
  const hasModuleAccess = (moduleName: string) =>
    user?.roles?.some((rol: any) =>
      rol.modulos?.some((modulo: any) =>
        modulo.nombre === moduleName && modulo.permisos?.some((p: any) => p.nombre === 'ver')
      )
    );

  if (hasModuleAccess('Dashboard')) return '/dashboard';
  if (hasModuleAccess('Mi Perfil')) return '/dashboard/miembro';
  return '/';
}

const biblicalQuotes = [
  {
    text: "Por tanto, id y haced discípulos de todas las naciones.",
    reference: "Mateo 28:19"
  },
  {
    text: "Porque donde están dos o tres congregados en mi nombre, allí estoy yo en medio de ellos.",
    reference: "Mateo 18:20"
  },
  {
    text: "De tal manera amó Dios al mundo, que ha dado a su Hijo unigénito.",
    reference: "Juan 3:16"
  },
  {
    text: "Mas a todos los que le recibieron, a los que creen en su nombre, les dio potestad de ser hechos hijos de Dios.",
    reference: "Juan 1:12"
  },
  {
    text: "Y sabemos que a los que aman a Dios, todas las cosas les ayudan a bien.",
    reference: "Romanos 8:28"
  }
];

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error en el login');
      }

      const response_data = await response.json();
      const data = response_data.data; // Extraer los datos del wrapper
      
      // Guardar token y datos del usuario en localStorage
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('userData', JSON.stringify(data.usuario));
      
      // Determinar ruta de inicio según permisos del usuario
      const targetRoute = getInitialRouteForUser(data.usuario);
      navigate(targetRoute);
      
    } catch (error: any) {
      console.error('Error en login:', error);
      setError(error.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Limpiar error al escribir
    if (error) setError(null);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prevIndex) => 
        (prevIndex + 1) % biblicalQuotes.length
      );
    }, 5000); // Cambia cada 5 segundos

    return () => clearInterval(interval);
  }, []);

  const handleGoogleLogin = () => {
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Church Information */}
      <div className="hidden lg:flex lg:w-[65%] relative overflow-hidden bg-stone-100">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-stone-400/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-40 right-20 w-40 h-40 bg-stone-300/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-stone-400/10 rounded-full blur-xl"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center px-16 py-12 h-screen w-full">
          {/* Spiritual Journey Animation */}
          <div className="relative w-full flex flex-col items-center justify-center">
            {/* Animated Cross */}
            <div className="absolute -top-16 -right-16 opacity-10">
              <svg className="w-40 h-40 text-white animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 7h-4V3a1 1 0 0 0-2 0v4H7a1 1 0 0 0 0 2h4v8H7a1 1 0 0 0 0 2h4v4a1 1 0 0 0 2 0v-4h4a1 1 0 0 0 0-2h-4V9h4a1 1 0 0 0 0-2Z"/>
              </svg>
            </div>

            {/* Main Content with Floating Animation */}
            <div className="space-y-10 animate-float w-full max-w-2xl mx-auto text-center">
              {/* Title with Sacred Typography */}
              <div className="text-center space-y-4">
                <div className="relative">
                  <h1 className="text-6xl font-light text-gray-900 mb-4 tracking-wide">
                    Conecta
                  </h1>
                  <div className="flex items-center justify-center space-x-4">
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent flex-1 max-w-24"></div>
                    <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-red-500 to-blue-600 bg-clip-text text-transparent">
                      con tu Iglesia
                    </h2>
                    <div className="h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent flex-1 max-w-24"></div>
                  </div>
                </div>
                
                <p className="text-lg text-gray-600 max-w-lg mx-auto leading-relaxed">
                  Una comunidad donde el amor de Cristo 
                  <span className="text-purple-600 font-semibold"> transforma vidas </span>
                  y fortalece la fe.
                </p>
              </div>

              {/* Sacred Elements - Centered Approach */}
              <div className="flex justify-center">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl">
                  {[
                    { title: "Fe", subtitle: "Creyendo en lo imposible", delay: "delay-200" },
                    { title: "Esperanza", subtitle: "Luz en la oscuridad", delay: "delay-400" },
                    { title: "Amor", subtitle: "El corazón de todo", delay: "delay-600" }
                  ].map((element, index) => (
                    <div key={index} className={`group animate-slide-up ${element.delay} text-center`}>
                      <div className="flex flex-col items-center space-y-4">
                        {/* Sacred Geometry Circle */}
                        <div className="w-16 h-16 rounded-full bg-stone-200 relative flex items-center justify-center shadow-sm group-hover:scale-110 transition-all duration-500 mx-auto">
                          <div className="w-8 h-8 rounded-full border-2 border-stone-400/60 relative">
                            <div className="absolute inset-1 rounded-full bg-stone-400/30 animate-ping"></div>
                          </div>
                        </div>
                        
                        <div className="text-center">
                          <h3 className="text-2xl font-serif font-light text-stone-800 mb-2 group-hover:text-purple-700 transition-colors">
                            {element.title}
                          </h3>
                          <p className="text-sm text-stone-500">
                            {element.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sacred Scriptures - Divine Presentation */}
          <div className="mt-16 relative w-full flex justify-center">
            {/* Sacred Frame */}
            <div className="relative p-10 bg-gradient-to-br from-white/5 via-white/10 to-white/5 rounded-3xl backdrop-blur-xl border border-white/20 shadow-2xl overflow-hidden max-w-2xl w-full">
              
              {/* Divine Light Rays */}
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-yellow-300 via-transparent to-transparent transform -translate-x-1/2 animate-pulse"></div>
                <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent transform -translate-y-1/2 animate-pulse delay-1000"></div>
              </div>

              {/* Sacred Geometry Background */}
              <div className="absolute inset-0 opacity-5">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="0.5" className="animate-spin-slow"/>
                  <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.3" className="animate-spin-reverse-slow"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="0.2" className="animate-spin-slow"/>
                </svg>
              </div>
              
              {/* Scripture Content */}
              <div className="relative z-10 text-center space-y-6">
                {/* Holy Symbol */}
                <div className="flex justify-center mb-6">
                  <div className="w-10 h-10 relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-yellow-400 to-blue-400 rounded-full animate-spin-slow"></div>
                    <div className="absolute inset-1 bg-gradient-to-br from-white/80 to-white/60 rounded-full flex items-center justify-center">
                      <div className="w-5 h-5 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full opacity-80"></div>
                    </div>
                  </div>
                </div>

                {/* Animated Scripture */}
                <div className="space-y-4">
                  <blockquote 
                    key={currentQuoteIndex}
                    className="text-lg font-light text-gray-800 leading-relaxed tracking-wide animate-fade-in-scale max-w-md mx-auto"
                  >
                    "{biblicalQuotes[currentQuoteIndex].text}"
                  </blockquote>
                  
                  <div className="flex items-center justify-center space-x-3">
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent flex-1 max-w-16"></div>
                    <cite className="text-purple-600 font-medium text-sm tracking-wider">
                      {biblicalQuotes[currentQuoteIndex].reference}
                    </cite>
                    <div className="h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent flex-1 max-w-16"></div>
                  </div>
                </div>

                {/* Divine Progress Indicators */}
                <div className="flex justify-center space-x-2 mt-6">
                  {biblicalQuotes.map((_, index) => (
                    <div 
                      key={index}
                      className={`relative transition-all duration-500 ${
                        index === currentQuoteIndex ? 'scale-125' : 'scale-100'
                      }`}
                    >
                      <div 
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${
                          index === currentQuoteIndex 
                            ? 'bg-gradient-to-r from-purple-400 to-blue-400 shadow-lg' 
                            : 'bg-gray-300/50'
                        }`}
                      />
                      {index === currentQuoteIndex && (
                        <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 animate-ping"></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Elegant Home Button */}
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => navigate('/')}
                    className="group relative px-8 py-3 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-indigo-500/20 rounded-2xl backdrop-blur-sm border border-purple-300/30 hover:from-purple-500/30 hover:via-blue-500/30 hover:to-indigo-500/30 transition-all duration-500 transform hover:scale-105 hover:shadow-xl"
                  >
                    {/* Sacred Glow Effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-400/10 via-blue-400/10 to-indigo-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                    
                    <div className="relative flex items-center space-x-3">
                      {/* Holy Symbol */}
                      <div className="w-5 h-5 relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full animate-pulse"></div>
                        <div className="absolute inset-0.5 bg-white/80 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      
                      <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600 transition-colors duration-300">
                        Regresar al Inicio
                      </span>
                      
                      {/* Floating Cross */}
                      <div className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                        <svg className="w-full h-full text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17 7h-4V3a1 1 0 0 0-2 0v4H7a1 1 0 0 0 0 2h4v8H7a1 1 0 0 0 0 2h4v4a1 1 0 0 0 2 0v-4h4a1 1 0 0 0 0-2h-4V9h4a1 1 0 0 0 0-2Z"/>
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Floating Particles */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute w-1 h-1 bg-white/60 rounded-full animate-float-particle opacity-40`}
                    style={{
                      left: `${25 + i * 20}%`,
                      top: `${30 + (i % 2) * 40}%`,
                      animationDelay: `${i * 0.7}s`,
                      animationDuration: `${3 + i}s`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="w-full lg:w-[35%] flex items-center justify-center px-8 lg:px-12 h-screen bg-stone-50">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and Header */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src={logoIglesia} 
                alt="Logo Iglesia Cristiana Cuadrangular" 
                className="w-24 h-24 sm:w-32 sm:h-32 object-contain"
              />
            </div>
            
            <h3 className="text-2xl font-serif font-bold text-stone-800 mb-2">
              Iglesia Cristiana Cuadrangular
            </h3>
            <h4 className="text-lg font-semibold text-stone-600 mb-4">
              Central de Apartadó
            </h4>
            <p className="text-purple-700 font-medium">¡Bienvenido hermano!</p>
            <p className="text-sm text-stone-500 mt-2">
              Accede al sistema de administración de la iglesia
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                Email o Número de Documento
              </label>
              <input
                id="email"
                name="email"
                type="text"
                autoComplete="username"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-white"
                placeholder="tu@email.com o 1234567890"
              />
              <p className="mt-1 text-xs text-stone-500">
                Puedes iniciar sesión con tu correo electrónico o con tu número de documento.
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-4 border border-stone-300 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-700 transition-all duration-300 text-stone-900 bg-white pr-12"
                  placeholder="••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {showPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 shadow-sm ${
                isLoading
                  ? 'bg-stone-400 cursor-not-allowed'
                  : 'bg-purple-800 hover:bg-purple-700'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Iniciando sesión...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <span>Iniciar Sesión</span>
                  <span className="ml-2">→</span>
                </div>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 text-stone-500 bg-white">
                  O continuar con
                </span>
              </div>
            </div>

            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 py-4 px-6 border border-stone-300 rounded-xl bg-white text-stone-700 hover:bg-stone-50 transition-all duration-300 transform hover:scale-105"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continuar con Google</span>
            </button>

            {/* Footer */}
            <div className="text-center space-y-3">
              <p className="text-sm text-stone-500">
                Plataforma de gestión premium
              </p>
              
              <p className="text-sm text-stone-600">
                ¿Olvidaste tu contraseña?{' '}
                <a href="#" className="text-purple-700 hover:text-purple-600 font-medium transition-colors">
                  Recuperar aquí
                </a>
              </p>
            </div>

            {/* Volver al inicio */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 text-stone-500 hover:text-stone-700 transition-colors duration-300 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Volver al inicio
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;